import { EventContentArg } from '@fullcalendar/core'
import { trucktypes } from 'constants/trucktypes'
import { useEffect } from 'react'
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle'
import { ILocationAvailabilities } from 'interfaces/locations'
import { getMinutesDifference } from 'utils/date'

export default function useCalendar() {
  const renderMonthlyEventContent = (eventInfo: EventContentArg) => {
    const [orderId, , lastName] = eventInfo.event.title.split(' - ')
    return (
      <div className="whitespace-normal">
        {orderId} - {lastName}
      </div>
    )
  }

  const renderWeeklyEventContent = (eventInfo: EventContentArg) => {
    const { name, orderNumber, truckAssignments, status } =
      eventInfo.event.extendedProps
    const totalCalculatedTime = truckAssignments.reduce(
      (acc: number, assignment: any) => {
        return acc + assignment.calculated_time
      },
      0
    )
    const isMultipleTrucks = truckAssignments.length > 1

    return (
      <div className="fc-custom-weekly-event flex flex-col gap-2">
        <div className="leading-tight">
          <div className="">#{orderNumber}</div>
          <div
            style={{ color: status === 'completed' ? '#81B57F' : '#7F96B5' }}
            className="text-[11px] whitespace-normal"
          >
            {name}
          </div>
        </div>
        {isMultipleTrucks ? (
          <span className="absolute hidden right-1 top-1 xl:flex flex-col">
            <AirportShuttleIcon />
            <AirportShuttleIcon />
          </span>
        ) : null}
        <div>{totalCalculatedTime} min</div>
      </div>
    )
  }

  const weeklyEventsFiltered = (
    events: any[],
    dayStart: Date,
    _dayEnd: Date
  ) => {
    const dayStartString = dayStart.toISOString().split('T')[0]
    return events
      .filter(function (event: any) {
        const instance = event._instance
        const instanceStartString = instance.range.start
          .toISOString()
          .split('T')[0]
        return dayStartString === instanceStartString
      })
      .map((event: any) => {
        const def = event._def
        return def
      })
  }
  const renderWeeklyGroupedEvents = (
    events: any[],
    dayWrapperElement: Element,
    dayEventsWrapperElement: Element,
    truckPayload: any,
    dayStart: Date
  ) => {
    if (events.length) {
      // Group events by truck type
      const groupedEvents = events.reduce((acc: any, event: any) => {
        event.extendedProps.truckAssignments.forEach((assignment: any) => {
          const truckType = assignment.class.truck_type

          if (!acc[truckType]) {
            acc[truckType] = []
          }

          acc[truckType].push({ ...event })
        })

        return acc
      }, {})

      // Create elements for each truck type
      Object.keys(groupedEvents).forEach((truckTypeKey) => {
        const truckType = trucktypes[truckTypeKey === '1' ? 1 : 0]
        const truckTypeElement = document.createElement('div')
        truckTypeElement.classList.add('truck-type')

        const groupedCorrectEvents = groupedEvents[truckTypeKey].map(
          (groupedEt: any) => {
            const formattedStart = dayStart
              .toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })
              .replace(/\//g, '-')

            const assignedTrucksAtLocation =
              groupedEt?.extendedProps?.assignedTrucksAtLocation.filter(
                (assignment: any) =>
                  assignment.class.truck_type === Number(truckTypeKey)
              )

            const trucksOverridden = assignedTrucksAtLocation.map(
              (AssTrAtLo: any) => {
                const checkIfTruckIsOverridden = truckPayload?.find(
                  (override: any) => {
                    const findId = override.truck_id === AssTrAtLo.class_id
                    return findId
                  }
                )

                if (checkIfTruckIsOverridden) {
                  return {
                    ...AssTrAtLo,
                    minute_capacity:
                      checkIfTruckIsOverridden.availability[formattedStart]?.[0]
                        .minutes_available
                  }
                }

                const getDayOfWeek = new Date(dayStart).getDay()
                const getLocationAvailability =
                  groupedEt?.extendedProps?.locationAvailabilities.find(
                    (LoAv: ILocationAvailabilities) => {
                      return LoAv.day_of_week === getDayOfWeek
                    }
                  )

                const minute_capacity = getMinutesDifference(
                  getLocationAvailability.working_time_end,
                  getLocationAvailability.working_time_start
                )

                return {
                  ...AssTrAtLo,
                  minute_capacity
                }
              }
            )
            return {
              ...groupedEt,
              truckAssignments:
                groupedEt?.extendedProps?.truckAssignments.filter(
                  (assignment: any) =>
                    assignment.class.truck_type === Number(truckTypeKey)
                ),
              assignedTrucksAtLocation: trucksOverridden
            }
          }
        )

        const totalCalculatedTime = groupedCorrectEvents.reduce(
          (acc: number, event: any) => {
            return (
              acc +
              (event?.extendedProps?.status !== 'completed'
                ? event?.extendedProps?.truckAssignments.reduce(
                    (acc: number, assignment: any) =>
                      acc + assignment.calculated_time,
                    0
                  )
                : 0)
            )
          },
          0
        )
        console.log(
          '🚀 -> l/s/m/A/l/h/useCalendar.tsx:187 -> groupedCorrectEvents: ',
          groupedCorrectEvents
        )

        const truckCapacity =
          groupedCorrectEvents[0]?.assignedTrucksAtLocation.reduce(
            (acc: number, assignment: any) => acc + assignment.minute_capacity,
            0
          )

        const remainingTime = truckCapacity - totalCalculatedTime

        // Truck type header with title and capacity
        truckTypeElement.innerHTML = `
                <div class="truck-type--title">
                    <span>${truckType}</span>
                    <span>${remainingTime || 0}/${
                      truckCapacity || 0
                    }</span> <!-- Update this capacity value as necessary -->
                </div>
            `

        const truckEventsContainer = document.createElement('div')
        truckEventsContainer.classList.add('truck-type--events')

        groupedCorrectEvents.forEach((event: any) => {
          const { orderId, orderNumber, name, status } = event.extendedProps

          const truckMin = event.truckAssignments[0].calculated_time

          const eventElement = document.createElement('a')
          eventElement.className = `
                    fc-event fc-event-start fc-event-end fc-event-past fc-daygrid-event fc-daygrid-dot-event
                    ${status === 'completed' ? 'fc-event-completed' : ''}
                `
          eventElement.href = `/admin/orders/${orderId}`

          // Event HTML structure
          eventElement.innerHTML = `
                    <div class="fc-custom-weekly-event flex flex-col gap-2 relative">
                        <div class="leading-tight">
                            <div>#${orderNumber}</div>
                            <div class="text-[11px] whitespace-normal" style="color: ${
                              status === 'completed' ? '#81B57F' : '#7F96B5'
                            }">
                                ${name}
                            </div>
                        </div>
                        <div>${truckMin} min</div>
                    </div>
                `

          truckEventsContainer.appendChild(eventElement)
        })

        truckTypeElement.appendChild(truckEventsContainer)
        dayWrapperElement.appendChild(truckTypeElement)
        dayEventsWrapperElement.remove() // Remove the original wrapper to avoid duplication
      })
    }
  }

  const selectOrderViewButtonDOM = () => {
    const orderViewButton = document.querySelector('.fc-orderView-button')
    const truckViewButton = document.querySelector('.fc-truckView-button')
    orderViewButton?.classList.add('active')
    truckViewButton?.classList.remove('active')
  }

  const selectTruckViewButtonDOM = () => {
    const orderViewButton = document.querySelector('.fc-orderView-button')
    const truckViewButton = document.querySelector('.fc-truckView-button')
    orderViewButton?.classList.remove('active')
    truckViewButton?.classList.add('active')
  }

  useEffect(() => {
    const orderViewButton = document.querySelector('.fc-orderView-button')
    orderViewButton?.classList.add('active')
  }, [])

  return {
    renderMonthlyEventContent,
    renderWeeklyEventContent,
    weeklyEventsFiltered,
    renderWeeklyGroupedEvents,
    selectOrderViewButtonDOM,
    selectTruckViewButtonDOM
  }
}
